/**
 * Actions Provider
 *
 * Shared state bridge between the actions_selector_ui chat tool and the
 * Control Panel's ActionsSection. Allows selected CleanupActions to flow
 * from the generative UI component into the right sidebar in real-time.
 */

import React, { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from 'react';
import { appConfig } from '@/config/app-config';
import { STORAGE_KEYS } from '@/config/storage-config';
import { useAppSelector } from '@/stores/store';
import { selectSelectedAgent } from '@/stores/selectors/agent-selectors';
import { selectCurrentThreadId } from '@/stores/selectors/chat-selectors';
import type { CleanupAction } from '@/components/control-panel/control-panel-dummy-data';

export interface DiscoveredCounts {
  optimization: number;
  cleanup: number;
  review: number;
}

export interface ActionsContextValue {
  actions: CleanupAction[];
  addActions: (newActions: CleanupAction[]) => void;
  /** Remove by id. Pass resourceName as fallback for when the context uses a
   *  different id than the caller (e.g. plan-builder agent IDs vs context IDs). */
  removeAction: (id: string, resourceName?: string) => void;
  loadActions: (threadId: string) => Promise<void>;
  clearActions: () => void;
  /** Counts of discovered findings per step — separate from confirmed actions. */
  discoveredCounts: DiscoveredCounts;
  /** selectorId should be the messageId of the actions-selector card — prevents
   *  double-counting when the same step's card appears twice in chat history. */
  addDiscoveredCounts: (counts: DiscoveredCounts, selectorId: string) => void;
  /**
   * Resets discoveredCounts to zero and clears reportedSelectorIds, then
   * increments refreshToken so still-mounted actions_selector_ui cards
   * re-run their reporting effect and rebuild the totals from scratch.
   * Call this after a rewind so removed cards don't leave stale counts.
   */
  resetDiscoveredCounts: () => void;
  /** Monotonically-increasing token; incremented by resetDiscoveredCounts. */
  refreshToken: number;
}

const ActionsContext = createContext<ActionsContextValue | null>(null);

interface ActionsProviderProps {
  children: ReactNode;
}

export function ActionsProvider({ children }: ActionsProviderProps) {
  const [actions, setActions] = useState<CleanupAction[]>([]);
  const [discoveredCounts, setDiscoveredCounts] = useState<DiscoveredCounts>({ optimization: 0, cleanup: 0, review: 0 });
  const [refreshToken, setRefreshToken] = useState(0);
  const selectedAgent = useAppSelector(selectSelectedAgent);
  const currentThreadId = useAppSelector(selectCurrentThreadId);
  // Tracks which selector card messageIds have already reported counts this session.
  // Prevents double-counting when the same step's card appears twice in chat history.
  const reportedSelectorIds = useRef(new Set<string>());

  // Keep a ref so loadActions never changes reference (avoids spurious reloads
  // from Redux state updates mid-session that would clear in-memory actions).
  const selectedAgentRef = useRef(selectedAgent);
  useEffect(() => {
    selectedAgentRef.current = selectedAgent;
  }, [selectedAgent]);

  // Reset discovered counts when the thread changes so the sidebar counters start
  // fresh for each conversation (historical cards re-mount and re-report via useRef).
  const prevThreadIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (currentThreadId !== prevThreadIdRef.current) {
      prevThreadIdRef.current = currentThreadId;
      if (currentThreadId) {
        setDiscoveredCounts({ optimization: 0, cleanup: 0, review: 0 });
        reportedSelectorIds.current = new Set<string>();
      }
    }
  }, [currentThreadId]);

  const addActions = useCallback((newActions: CleanupAction[]) => {
    setActions((prev) => {
      // Deduplicate by id — merge incoming actions, replacing existing ones with same id
      const existingIds = new Set(prev.map((a) => a.id));
      const fresh = newActions.filter((a) => !existingIds.has(a.id));
      return [...prev, ...fresh];
    });
  }, []);

  const removeAction = useCallback((id: string, resourceName?: string) => {
    setActions((prev) => {
      // Try exact ID match first; if not found, fall back to name match.
      // This handles cases where the caller uses agent-generated IDs that differ
      // from the context IDs (loaded from DB via loadActions or addActions).
      const hasExactMatch = prev.some((a) => a.id === id);
      if (hasExactMatch) return prev.filter((a) => a.id !== id);
      if (resourceName) return prev.filter((a) => a.name !== resourceName);
      return prev;
    });
    // PATCH the backend using the caller-supplied id (agent action IDs = DB action_id).
    if (currentThreadId) {
      const agentId = selectedAgent?.id || 'optimize-clean-up-agent';
      const token = localStorage.getItem(STORAGE_KEYS.TOKEN) || '';
      fetch(
        `${appConfig.API_BASE_URL}/agents/${agentId}/threads/${currentThreadId}/actions/${id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...(token && { Authorization: `Bearer ${token}` }) },
          body: JSON.stringify({ status: 'skipped' }),
        }
      ).catch(() => { /* non-critical — in-memory removal already done */ });
    }
  }, [currentThreadId, selectedAgent]);

  const loadActions = useCallback(async (threadId: string) => {
    setActions([]);  // Clear stale state immediately on thread switch
    const agentId = selectedAgentRef.current?.id || 'optimize-clean-up-agent';
    const token = localStorage.getItem(STORAGE_KEYS.TOKEN) || '';
    try {
      const response = await fetch(`${appConfig.API_BASE_URL}/agents/${agentId}/threads/${threadId}/actions?status=pending`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { Authorization: `Bearer ${token}` }),
        },
      });
      if (!response.ok) return;
      const data = await response.json();
      const loaded: CleanupAction[] = (data.actions ?? []).map((row: any) => ({
        id: row.action_id,
        name: row.resource_name,
        description: row.description ?? '',
        category: row.resource_type
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c: string) => c.toUpperCase()),
        type: (row.action_type === 'delete' || row.action_type === 'update') ? 'cleanup' : row.action_type === 'review' ? 'review' : 'optimization',
        taskId: row.rule_id,
        selectedAt: new Date().toISOString(),
      }));
      setActions(loaded);
    } catch {
      // Silently ignore — in-memory state is still valid
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const clearActions = useCallback(() => {
    setActions([]);
    setDiscoveredCounts({ optimization: 0, cleanup: 0, review: 0 });
    reportedSelectorIds.current = new Set<string>();
  }, []);

  const resetDiscoveredCounts = useCallback(() => {
    setDiscoveredCounts({ optimization: 0, cleanup: 0, review: 0 });
    reportedSelectorIds.current = new Set<string>();
    setRefreshToken((t) => t + 1);
  }, []);

  const addDiscoveredCounts = useCallback((counts: DiscoveredCounts, selectorId: string) => {
    if (reportedSelectorIds.current.has(selectorId)) return;
    reportedSelectorIds.current.add(selectorId);
    setDiscoveredCounts((prev) => ({
      optimization: prev.optimization + counts.optimization,
      cleanup: prev.cleanup + counts.cleanup,
      review: prev.review + counts.review,
    }));
  }, []);

  const contextValue: ActionsContextValue = {
    actions,
    addActions,
    removeAction,
    loadActions,
    clearActions,
    discoveredCounts,
    addDiscoveredCounts,
    resetDiscoveredCounts,
    refreshToken,
  };

  return <ActionsContext.Provider value={contextValue}>{children}</ActionsContext.Provider>;
}

export function useActions(): ActionsContextValue {
  const context = useContext(ActionsContext);
  if (!context) {
    throw new Error('useActions must be used within an ActionsProvider');
  }
  return context;
}

export function useOptionalActions(): ActionsContextValue | null {
  return useContext(ActionsContext);
}
